import { findStepType } from '@vigeo/shared';
import type { ReviewItem } from '@/lib/api';

/**
 * How a review item reads to HR (checkpoint 7C). Every value here is a field the review
 * API already returns, or a pure projection of one — nothing is stored, nothing is
 * guessed. Timestamps, attempts and routing come from the wire; there are no reviewer
 * assignments, departments or workflow states because the API carries none.
 */

const DAY_MS = 86_400_000;

/** From this many days waiting, the queue says so out loud. */
export const WAITING_ATTENTION_DAYS = 3;

/** Whole days, because "3 days" is what a reviewer acts on and "76 hours" is not. */
export function ageInDays(submittedAt: string | null, now: number): number | null {
  if (submittedAt === null) return null;
  const parsed = Date.parse(submittedAt);
  return Number.isNaN(parsed) ? null : Math.max(0, Math.floor((now - parsed) / DAY_MS));
}

export function ageLabel(days: number | null): string {
  if (days === null) return 'Submitted';
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return `${days} days ago`;
}

const dateFormat = new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeZone: 'UTC' });

/** The submission date as a date, from the wire timestamp; null when the API sent none. */
export function submittedOn(submittedAt: string | null): string | null {
  if (submittedAt === null) return null;
  const parsed = Date.parse(submittedAt);
  return Number.isNaN(parsed) ? null : dateFormat.format(parsed);
}

/** The step type's own label from the shared registry; a type nobody registered still reads as a submission. */
export function stepTypeLabel(type: string): string {
  return findStepType(type)?.label ?? 'Submission';
}

/**
 * Where the file goes once approved. I-9s and health records route to segregated folders,
 * never the main personnel folder — the reviewer should know that before approving.
 */
export function routingLabel(segregated: boolean): string {
  return segregated ? 'Segregated folder (I-9 or health record)' : 'Personnel folder';
}

/** `attempt` counts earlier tries; the first submission is attempt zero. */
export function attemptLabel(attempt: number): string {
  return attempt > 0 ? `Attempt ${attempt + 1}` : 'First attempt';
}

export type ChipTone = 'neutral' | 'important' | 'warning';

export interface ItemChip {
  readonly key: string;
  readonly label: string;
  readonly tone: ChipTone;
}

/** The short signals a row carries; each one is a wire field, in the order HR needs them. */
export function itemChips(item: ReviewItem, now: number): readonly ItemChip[] {
  const chips: ItemChip[] = [];
  if (item.segregated) chips.push({ key: 'segregated', label: 'Segregated file', tone: 'important' });
  if (item.attempt > 0) chips.push({ key: 'attempt', label: attemptLabel(item.attempt), tone: 'neutral' });
  const days = ageInDays(item.submittedAt, now);
  if (days !== null && days >= WAITING_ATTENTION_DAYS) chips.push({ key: 'waiting', label: `Waiting ${days} days`, tone: 'warning' });
  return chips;
}

/** One line for the page header: the real count on this page and how long the oldest has waited. */
export function queueContext(items: readonly ReviewItem[], now: number, more: boolean): string {
  if (items.length === 0) return 'Nothing waiting';
  const oldest = items.reduce<number | null>((acc, item) => {
    const days = ageInDays(item.submittedAt, now);
    return days === null ? acc : Math.max(acc ?? 0, days);
  }, null);
  const count = `${items.length} waiting${more ? ' on this page, more to load' : ''}`;
  if (oldest === null || oldest === 0) return count;
  return `${count} · oldest ${oldest === 1 ? '1 day' : `${oldest} days`}`;
}
